import {
  SiteAcceptReportInput,
  SiteQuantity,
  type SiteReportSnapshot,
  type SiteReviewContext,
  SiteReviewMetadata,
  SiteReviewReportInput,
  siteQuantityUnits,
} from '@epc/contracts';
import { z } from 'zod';
import type { OfflineScope } from '../offline/store';
import { reportResponse } from '../transport';

export const reviewContextResponse = SiteReviewMetadata.extend({ report: reportResponse });
export type DecisionAction = 'verify' | 'return' | 'accept';
export interface ReviewInput {
  lines: Array<{ lineId: string; quantity: string; reason: string; resourcesConfirmed: boolean }>;
  returnReason: string;
}
export interface ReviewCommand {
  key: string;
  action: DecisionAction;
  requestJson: string;
  state: 'pending' | 'auth_paused' | 'rejected' | 'conflict' | 'done';
  error: string | null;
  result: SiteReportSnapshot | null;
}
export function initialReviewInput(report: SiteReportSnapshot): ReviewInput {
  return {
    lines: report.draft.lines.map((line) => ({
      lineId: line.id,
      quantity: line.claimedQuantity,
      reason: '',
      resourcesConfirmed: false,
    })),
    returnReason: '',
  };
}
export function pendingDecision(command: ReviewCommand | null) {
  return Boolean(command && ['pending', 'auth_paused'].includes(command.state));
}
export function prepareDecision(
  context: SiteReviewContext,
  scope: OfflineScope,
  input: ReviewInput,
  action: DecisionAction,
  online: boolean,
  key: string = crypto.randomUUID(),
  now = new Date().toISOString(),
): ReviewCommand {
  const report = context.report;
  if (!online) throw new Error('Решение доступно только при подключении к сети');
  if (report.tenantId !== scope.tenantId || report.authorId === scope.userId || !context.capabilities[action])
    throw new Error('Решение недоступно. Обновите отчёт и проверьте права');
  const envelope = {
    schemaVersion: 1,
    clientId: report.clientId,
    clientOperationId: key,
    expectedRevision: report.revision,
    sourceRevisions: context.sourceRevisions,
    recordedOffline: false,
    occurredAt: now,
    effectiveAt: report.draft.reportDay,
  };
  let body: unknown;
  if (action === 'accept') body = SiteAcceptReportInput.parse(envelope);
  else if (action === 'return') {
    if (!input.returnReason.trim()) throw new Error('Укажите причину возврата');
    body = SiteReviewReportInput.parse({ ...envelope, action, reason: input.returnReason });
  } else {
    const entries = new Map(input.lines.map((line) => [line.lineId, line]));
    if (entries.size !== input.lines.length || entries.size !== report.draft.lines.length)
      throw new Error('Проверьте каждую работу ровно один раз');
    const lines = report.draft.lines.map((line, i) => {
      const entry = entries.get(line.id);
      const parsed = SiteQuantity.safeParse(entry?.quantity.trim().replace(',', '.'));
      if (!entry || !parsed.success)
        throw new Error(`Работа ${i + 1}: укажите неотрицательное количество, до 6 знаков после запятой`);
      if (siteQuantityUnits(parsed.data) > siteQuantityUnits(line.claimedQuantity))
        throw new Error(`Работа ${i + 1}: проверенный объём не может превышать заявленный`);
      if (siteQuantityUnits(parsed.data) < siteQuantityUnits(line.claimedQuantity) && !entry.reason.trim())
        throw new Error(`Работа ${i + 1}: объясните уменьшение объёма`);
      if (!entry.resourcesConfirmed) throw new Error(`Работа ${i + 1}: подтвердите проверку ресурсов`);
      return { lineId: line.id, quantity: parsed.data, reason: entry.reason, resourcesConfirmed: true };
    });
    body = SiteReviewReportInput.parse({ ...envelope, action, lines });
  }
  return { key, action, requestJson: JSON.stringify(body), state: 'pending', error: null, result: null };
}

const scheduleReceipt = z.object({
  sourceId: z.uuid(),
  sourceRevision: z.number().int(),
  scheduleId: z.uuid(),
  scheduleRevision: z.number().int(),
  applied: z.literal(true),
  entries: z.array(
    z.object({
      sourceLineId: z.uuid(),
      entryId: z.uuid(),
      businessEffectId: z.uuid(),
      quantity: SiteQuantity,
      unit: z.string(),
    }),
  ),
});
export function validateDecisionReceipt(
  response: unknown,
  base: SiteReportSnapshot,
  command: ReviewCommand,
  scope: OfflineScope,
  scheduleId: string,
) {
  const parsed = z
    .object({
      receiptId: z.uuid(),
      replayed: z.boolean(),
      result: z.object({
        report: reportResponse,
        scheduleReceipt: scheduleReceipt.nullable(),
        posting: z.object({ schedule: z.string() }),
      }),
    })
    .parse(response);
  const report = parsed.result.report;
  const body = JSON.parse(command.requestJson);
  const expectedState = { verify: 'verified', return: 'returned', accept: 'accepted' }[command.action];
  if (
    report.id !== base.id ||
    report.clientId !== base.clientId ||
    report.tenantId !== scope.tenantId ||
    report.authorId !== base.authorId ||
    report.authorId === scope.userId ||
    report.revision !== base.revision + 1 ||
    report.state !== expectedState ||
    report.decision?.actorId !== scope.userId ||
    report.decision.action !== command.action ||
    JSON.stringify(report.draft) !== JSON.stringify(base.draft)
  )
    throw new Error('Ответ относится к другому решению');
  if (command.action === 'return' && report.decision.reason !== body.reason)
    throw new Error('Другая причина возврата');
  const checks = command.action === 'verify' ? body.lines : base.verification;
  if (JSON.stringify(report.verification) !== JSON.stringify(checks))
    throw new Error('Другие проверенные объёмы');
  if (command.action === 'accept') {
    const receipt = parsed.result.scheduleReceipt;
    if (
      !receipt ||
      receipt.sourceId !== base.id ||
      receipt.sourceRevision !== report.revision ||
      receipt.scheduleId !== scheduleId ||
      receipt.scheduleRevision <= base.draft.scheduleRevision ||
      parsed.result.posting.schedule !== 'applied' ||
      JSON.stringify(report.accepted) !== JSON.stringify(base.verification) ||
      receipt.entries.length !== base.draft.lines.length ||
      new Set(receipt.entries.map((e) => e.sourceLineId)).size !== receipt.entries.length ||
      new Set(receipt.entries.map((e) => e.entryId)).size !== receipt.entries.length ||
      new Set(receipt.entries.map((e) => e.businessEffectId)).size !== receipt.entries.length
    )
      throw new Error('Нет полного подтверждения графика');
    for (const line of base.draft.lines) {
      const entry = receipt.entries.find((e) => e.sourceLineId === line.id);
      const amount = report.accepted?.find((v) => v.lineId === line.id)?.quantity;
      if (
        !entry ||
        amount === undefined ||
        entry.unit !== line.unit ||
        siteQuantityUnits(entry.quantity) !== siteQuantityUnits(amount)
      )
        throw new Error('График подтвердил другую строку');
    }
  } else if (report.accepted !== null || parsed.result.scheduleReceipt !== null)
    throw new Error('Преждевременное принятие');
  return report as SiteReportSnapshot;
}
